/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  Clock,
  Crown,
  Handshake,
  KeyRound,
  Lock,
  Phone,
  MessageCircle,
  Users,
} from "lucide-react";
import { adminApi } from "../auth/adminApi";
import { apiFetch } from "../auth/apiClient";
import { useAuth } from "../auth/AuthContext";
import "./MembersList.css";

const ROLE_LABEL = {
  PRIMARY_OWNER: "Primary Owner",
  SHARED_OWNER: "Shared Owner",
  RESIDENT: "Resident",
  TENANT: "Tenant",
};

const ROLE_BADGE_CLASS = {
  PRIMARY_OWNER: "vsm-badge-primary",
  RESIDENT: "vsm-badge-secondary",
  TENANT: "vsm-badge-tenant",
};

function RoleIcon({ role }) {
  const label = ROLE_LABEL[role] || role;
  let icon = null;
  let cls = "";
  if (role === "PRIMARY_OWNER") {
    icon = <Crown size={14} strokeWidth={2.2} />;
    cls = "vsm-role-icon-primary";
  } else if (role === "SHARED_OWNER") {
    icon = <Handshake size={14} strokeWidth={2.2} />;
    cls = "vsm-role-icon-shared";
  } else if (role === "RESIDENT") {
    icon = <Users size={14} strokeWidth={2.2} />;
    cls = "vsm-role-icon-secondary";
  } else if (role === "TENANT") {
    icon = <KeyRound size={14} strokeWidth={2.2} />;
    cls = "vsm-role-icon-tenant";
  } else {
    return null;
  }
  return (
    <span
      className={`vsm-role-icon ${cls}`}
      data-tooltip={label}
      aria-label={label}
    >
      {icon}
    </span>
  );
}

const STATUS_BADGE_CLASS = {
  APPROVED: "vsm-status-approved",
  PENDING: "vsm-status-pending",
  REJECTED: "vsm-status-rejected",
  REVOKED: "vsm-status-rejected",
};

function StatusIcon({ status }) {
  if (status === "APPROVED") {
    return (
      <span
        className="vsm-status-icon vsm-status-icon-approved"
        data-tooltip="Verified member"
        aria-label="Verified member"
      >
        <BadgeCheck size={16} strokeWidth={2.4} />
      </span>
    );
  }
  if (status === "PENDING") {
    return (
      <span
        className="vsm-status-icon vsm-status-icon-pending"
        data-tooltip="Pending approval"
        aria-label="Pending approval"
      >
        <Clock size={14} strokeWidth={2.2} />
      </span>
    );
  }
  return null;
}

async function fetchMembers(token, { view, page, pageSize, q, status }) {
  const params = new URLSearchParams({
    view,
    page: String(page),
    page_size: String(pageSize),
  });
  if (q) params.set("q", q);
  if (status && status !== "ALL") params.set("status", status);
  return apiFetch(`/api/residents?${params.toString()}`, {
    method: "GET",
    token,
  });
}

const PAGE_SIZE_GROUPED = 10;
const PAGE_SIZE_LIST = 25;

function PhoneCell({ m, canReveal = false }) {
  // Local override so an audited reveal unmasks the number in place without a
  // full list refetch.
  const [revealed, setRevealed] = useState(null); // unmasked phone string
  const [revealing, setRevealing] = useState(false);
  const phone = revealed != null ? revealed : m.user.phone_number || "";
  const visible = m.phone_visible || revealed != null;
  const digits = String(phone).replace(/\D/g, "").slice(-10);
  const canContact = visible && digits.length === 10;

  const reveal = async (e) => {
    e.stopPropagation();
    if (revealing) return;
    setRevealing(true);
    try {
      // Auto-grants + writes a PHONE_ACCESS_VIEWED audit row + notifies the
      // member, then re-reads the now-unmasked profile.
      await apiFetch(`/api/users/${m.user.id}/request-phone-access`, {
        method: "POST",
        authRequired: true,
        body: { reason: "Gate / operations contact" },
      });
      const u = await apiFetch(`/api/users/${m.user.id}`, {
        method: "GET",
        authRequired: true,
      });
      setRevealed(u.phone_number || "");
    } catch (_) {
      // leave masked on failure
    } finally {
      setRevealing(false);
    }
  };

  return (
    <span
      className={`vsm-phone ${
        visible ? "vsm-phone-visible" : "vsm-phone-masked"
      }`}
    >
      {visible ? (
        <Phone size={12} strokeWidth={2.2} />
      ) : (
        <Lock size={12} strokeWidth={2.2} />
      )}
      +91 {phone}
      {!visible && canReveal && (
        <button
          type="button"
          className="vsm-reveal-btn"
          onClick={reveal}
          disabled={revealing}
          title="Reveal contact (audited)"
        >
          {revealing ? "Revealing…" : "Reveal"}
        </button>
      )}
      {canContact && (
        <span className="vsm-phone-actions">
          <a
            href={`tel:+91${digits}`}
            className="vsm-contact-icon"
            aria-label="Call"
            title="Call"
            onClick={(e) => e.stopPropagation()}
          >
            <Phone size={13} />
          </a>
          <a
            href={`https://wa.me/91${digits}`}
            target="_blank"
            rel="noreferrer"
            className="vsm-contact-icon vsm-contact-wa"
            aria-label="WhatsApp"
            title="WhatsApp"
            onClick={(e) => e.stopPropagation()}
          >
            <MessageCircle size={13} />
          </a>
        </span>
      )}
    </span>
  );
}

function buildPageList(current, total) {
  // Compact pagination: always include 1, total, current ±1, with ellipses.
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i += 1) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}

function Pagination({
  page,
  totalPages,
  onPageChange,
  pageSize,
  totalItems,
  itemLabel,
  itemsOnPage,
}) {
  const start = (page - 1) * pageSize + 1;
  const end = start + itemsOnPage - 1;
  const pages = buildPageList(page, totalPages);
  return (
    <div className="vsm-pagination">
      <div className="vsm-pagination-info">
        Showing <strong>{start}</strong>–<strong>{end}</strong> of{" "}
        <strong>{totalItems}</strong> {itemLabel}
        {totalItems === 1 ? "" : "s"}
      </div>
      <div className="vsm-pagination-controls">
        <button
          className="vsm-page-btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`gap-${i}`} className="vsm-page-gap">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`vsm-page-btn ${p === page ? "active" : ""}`}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          )
        )}
        <button
          className="vsm-page-btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function BulkOnboardModal({ onClose, onCreated }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await apiFetch("/api/users/onboard-bulk", {
        method: "POST",
        formData: fd,
        authRequired: true,
      });
      setResult(r);
      if ((r.created || []).length > 0) onCreated();
    } catch (e) {
      setErr(e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const createdCount = result?.created?.length ?? 0;
  const skippedCount = result?.skipped?.length ?? 0;
  const errorCount = result?.errors?.length ?? 0;

  return (
    <div className="vs-login-overlay" onClick={onClose}>
      <div
        className="vs-login-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 500 }}
      >
        <div className="vs-login-header">
          <h2>Bulk onboard from .xlsx</h2>
          <button className="vs-login-close" onClick={onClose}>×</button>
        </div>

        {!result ? (
          <>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 14px" }}>
              Upload a spreadsheet with columns:{" "}
              <strong>Name</strong>, <strong>Mobile/Phone</strong>,{" "}
              <strong>Villa/Villa Number</strong>,{" "}
              <strong>Role</strong> (owner/member/tenant).
              Existing users are skipped silently.
            </p>
            <div className="vs-field">
              <label>Spreadsheet (.xlsx)</label>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx"
                className="vs-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
            {err && (
              <p style={{ color: "#b91c1c", fontSize: 13, margin: "8px 0 0" }}>{err}</p>
            )}
            <div className="vs-otp-actions">
              <button className="vs-btn-secondary" onClick={onClose}>Cancel</button>
              <button
                className="vsa-btn-approve"
                onClick={submit}
                disabled={!file || busy}
              >
                {busy ? "Uploading…" : "Upload & Onboard"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", gap: 12, margin: "0 0 16px", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 70, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: "#166534" }}>{createdCount}</div>
                <div style={{ fontSize: 12, color: "#4ade80" }}>Created</div>
              </div>
              <div style={{ flex: 1, minWidth: 70, background: "#fef9c3", border: "1px solid #fde047", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: "#854d0e" }}>{skippedCount}</div>
                <div style={{ fontSize: 12, color: "#a16207" }}>Skipped</div>
              </div>
              <div style={{ flex: 1, minWidth: 70, background: errorCount > 0 ? "#fef2f2" : "#f8fafc", border: errorCount > 0 ? "1px solid #fecaca" : "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: errorCount > 0 ? "#b91c1c" : "#94a3b8" }}>{errorCount}</div>
                <div style={{ fontSize: 12, color: errorCount > 0 ? "#ef4444" : "#94a3b8" }}>Errors</div>
              </div>
            </div>
            {errorCount > 0 && (
              <div style={{ maxHeight: 150, overflow: "auto", background: "#fef2f2", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
                {result.errors.map((e, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#b91c1c", padding: "3px 0", borderBottom: i < errorCount - 1 ? "1px solid #fecaca" : "none" }}>
                    Row {e.row}: {e.reason}
                  </div>
                ))}
              </div>
            )}
            <div className="vs-otp-actions">
              <button className="vs-btn-secondary" onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function MembersList({ canApprove = false }) {
  const { activeRole, isAdmin } = useAuth();
  // Operations roles (and admins) may reveal a member's masked contact; every
  // reveal is audited + the member is notified.
  const canReveal =
    isAdmin ||
    ["ADMIN", "FACILITY_MANAGER", "SECURITY_INCHARGE"].includes(activeRole);
  const [pageData, setPageData] = useState({
    view: "grouped",
    page: 1,
    page_size: PAGE_SIZE_GROUPED,
    total: 0,
    total_pages: 1,
    total_members: 0,
    items: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState("grouped");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [phoneRequests, setPhoneRequests] = useState([]);
  const [phoneRejectTarget, setPhoneRejectTarget] = useState(null);
  const [phoneRejectReason, setPhoneRejectReason] = useState("");
  const [phoneBusyId, setPhoneBusyId] = useState("");
  const [showBulkOnboard, setShowBulkOnboard] = useState(false);

  // Debounce the search box so we don't re-query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever a filter, the view, or search changes.
  useEffect(() => {
    setPage(1);
  }, [viewMode, statusFilter, debouncedSearch]);

  const pageSize = viewMode === "grouped" ? PAGE_SIZE_GROUPED : PAGE_SIZE_LIST;

  const load = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchMembers(token, {
        view: viewMode,
        page,
        pageSize,
        q: debouncedSearch,
        status: statusFilter,
      });
      setPageData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [viewMode, page, pageSize, debouncedSearch, statusFilter]);

  const loadPhoneRequests = useCallback(async () => {
    // Mobile-change requests are an admin-only approval queue; don't even
    // fetch them for non-approvers (the endpoint 403s for them anyway).
    if (!canApprove) return;
    try {
      const data = await apiFetch("/api/users/phone-requests", {
        authRequired: true,
      });
      setPhoneRequests(data.requests || []);
    } catch (_) {}
  }, [canApprove]);

  useEffect(() => {
    load();
    loadPhoneRequests();
  }, [load, loadPhoneRequests]);

  const approvePhoneRequest = async (id) => {
    setPhoneBusyId(id);
    try {
      await apiFetch(`/api/users/phone-requests/${id}/approve`, {
        method: "POST",
        authRequired: true,
      });
      await loadPhoneRequests();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setPhoneBusyId("");
    }
  };

  const submitPhoneRejection = async () => {
    if (!phoneRejectTarget) return;
    setPhoneBusyId(phoneRejectTarget.id);
    try {
      await apiFetch(`/api/users/phone-requests/${phoneRejectTarget.id}/reject`, {
        method: "POST",
        authRequired: true,
        body: { reason: phoneRejectReason },
      });
      setPhoneRejectTarget(null);
      setPhoneRejectReason("");
      await loadPhoneRequests();
    } catch (e) {
      setError(e.message);
    } finally {
      setPhoneBusyId("");
    }
  };

  const handleApprove = async (approvalId) => {
    setBusyId(approvalId);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      await adminApi.approve(approvalId, token);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId("");
    }
  };

  const submitReject = async () => {
    if (!rejecting) return;
    setBusyId(rejecting.approval_id);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      await adminApi.reject(rejecting.approval_id, rejectReason || null, token);
      setRejecting(null);
      setRejectReason("");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId("");
    }
  };

  // Drive stats off pageData.view (the shape we actually have) rather
  // than viewMode (the shape we WANT). During the brief gap between
  // toggling the view and the next fetch landing, the two disagree and
  // reading g.members.length on a flat row crashes. Defensive `?.` on
  // top guards against the very first render when items[i] hasn't been
  // shape-checked yet.
  const dataView = pageData.view;
  const villasOnPage = dataView === "grouped" ? pageData.items.length : null;
  const membersOnPage =
    dataView === "grouped"
      ? pageData.items.reduce((n, g) => n + (g?.members?.length || 0), 0)
      : pageData.items.length;
  const totalMembers =
    dataView === "grouped" ? pageData.total_members || 0 : pageData.total || 0;
  const totalVillas = dataView === "grouped" ? pageData.total || 0 : null;

  const renderRow = (m, villaNumber, showVilla = false) => (
    <div key={`${villaNumber}-${m.id}`} className="vsm-member">
      <div className="vsm-member-main">
        <div className="vsm-member-name">
          <Link to={`/profile/${m.user.id}`} className="vsm-name-link">
            {m.user.name || "Unnamed"}
          </Link>
          <RoleIcon role={m.role} />
          <StatusIcon status={m.approval_status} />
          {showVilla && (
            <Link
              to={`/villas/${encodeURIComponent(villaNumber)}`}
              className="vsm-villa-tag vsm-villa-tag-link"
            >
              {villaNumber}
            </Link>
          )}
        </div>
        <div className="vsm-member-meta">
          <PhoneCell m={m} canReveal={canReveal} />
          {m.tenant_end_date && <span>· until {m.tenant_end_date}</span>}
        </div>
      </div>
      {canApprove && m.approval_status === "PENDING" && m.approval_id && (
        <div className="vsm-member-actions">
          <button
            className="vsa-btn-approve"
            disabled={busyId === m.approval_id}
            onClick={() => handleApprove(m.approval_id)}
          >
            {busyId === m.approval_id ? "…" : "Approve"}
          </button>
          <button
            className="vsa-btn-reject"
            disabled={busyId === m.approval_id}
            onClick={() => {
              setRejecting(m);
              setRejectReason("");
            }}
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );

  const groupedItems = viewMode === "grouped" ? pageData.items : [];
  const flatRows = viewMode === "list" ? pageData.items : [];
  const isEmpty = pageData.items.length === 0;

  return (
    <div className="vsm-wrap">
      <div className="vsm-stats">
        {viewMode === "grouped" && (
          <div className="vsm-stat">
            <span className="vsm-stat-num">{totalVillas}</span>
            <span className="vsm-stat-lbl">
              {debouncedSearch || statusFilter !== "ALL"
                ? "Villas matching"
                : "Villas with members"}
            </span>
          </div>
        )}
        <div className="vsm-stat">
          <span className="vsm-stat-num">{totalMembers}</span>
          <span className="vsm-stat-lbl">
            {debouncedSearch || statusFilter !== "ALL"
              ? "Members matching"
              : "Total members"}
          </span>
        </div>
      </div>

      <div className="vsm-controls">
        <input
          type="text"
          className="vsm-search"
          placeholder="Search by villa number, name, or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="vsm-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="ALL">All statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <div className="vsm-toggle" role="tablist">
          <button
            className={viewMode === "grouped" ? "active" : ""}
            onClick={() => setViewMode("grouped")}
          >
            Grouped
          </button>
          <button
            className={viewMode === "list" ? "active" : ""}
            onClick={() => setViewMode("list")}
          >
            List
          </button>
        </div>
        {isAdmin && (
          <button
            className="vsm-bulk-onboard-btn"
            onClick={() => setShowBulkOnboard(true)}
            title="Bulk onboard from .xlsx"
          >
            Bulk onboard (.xlsx)
          </button>
        )}
      </div>
      {showBulkOnboard && (
        <BulkOnboardModal
          onClose={() => setShowBulkOnboard(false)}
          onCreated={load}
        />
      )}

      {canApprove && phoneRequests.length > 0 && (
        <div className="vsm-phone-section">
          <div className="vsm-phone-head">
            <h3>Mobile change requests</h3>
            <span className="vsm-phone-count">{phoneRequests.length} pending</span>
          </div>
          <div className="vsm-phone-list">
            {phoneRequests.map((r) => (
              <div key={r.id} className="vsm-phone-card">
                <div className="vsm-phone-main">
                  <div className="vsm-phone-name">
                    <Link to={`/profile/${r.user_id}`} className="vsm-name-link">
                      {r.user_name}
                    </Link>
                    <span className="vsm-badge">
                      {r.user_type === "PRIMARY_USER" ? "Primary" : "Secondary"}
                    </span>
                  </div>
                  <div className="vsm-phone-change">
                    <code>+91 {r.current_phone}</code> →{" "}
                    <code>+91 {r.requested_phone}</code>
                  </div>
                  {r.villas.length > 0 && (
                    <div className="vsm-phone-villas">
                      {r.villas
                        .map(
                          (v) =>
                            `${v.villa_number} (${
                              ROLE_LABEL[v.role] || v.role
                            })`
                        )
                        .join(" · ")}
                    </div>
                  )}
                </div>
                <div className="vsm-member-actions">
                  <button
                    className="vsa-btn-approve"
                    disabled={phoneBusyId === r.id}
                    onClick={() => approvePhoneRequest(r.id)}
                  >
                    {phoneBusyId === r.id ? "…" : "Approve"}
                  </button>
                  <button
                    className="vsa-btn-reject"
                    disabled={phoneBusyId === r.id}
                    onClick={() => {
                      setPhoneRejectTarget(r);
                      setPhoneRejectReason("");
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div className="vs-error">{error}</div>}
      {loading && <div className="vsm-empty">Loading…</div>}
      {!loading && isEmpty && (
        <div className="vsm-empty">
          {debouncedSearch || statusFilter !== "ALL"
            ? "No members match your search."
            : "No members registered yet."}
        </div>
      )}

      {/* Gate on pageData.view === viewMode so we never render grouped
          rows against a list layout (or vice versa) between viewMode
          flip and the next fetch completing. Without this guard,
          renderRow crashes on m.user.id because flat-mode expects
          MemberOut but grouped-mode items are {villa, members}. */}
      {!loading && viewMode === "grouped" && pageData.view === "grouped" && groupedItems.length > 0 && (
        <div className="vsm-groups">
          {groupedItems.map((g) => (
            <div key={g.villa.id} className="vsm-group">
              <div className="vsm-group-head">
                <h3>
                  <Link
                    to={`/villas/${encodeURIComponent(g.villa.villa_number)}`}
                    className="vsm-villa-link"
                  >
                    {g.villa.villa_number}
                  </Link>
                </h3>
                <span className="vsm-group-count">
                  {g.members.length} {g.members.length === 1 ? "member" : "members"}
                </span>
              </div>
              <div className="vsm-members">
                {g.members.map((m) => renderRow(m, g.villa.villa_number))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && viewMode === "list" && pageData.view === "list" && flatRows.length > 0 && (
        <div className="vsm-group">
          <div className="vsm-members">
            {flatRows.map((m) => renderRow(m, m.villa.villa_number, true))}
          </div>
        </div>
      )}

      {!loading && !isEmpty && pageData.total_pages > 1 && (
        <Pagination
          page={page}
          totalPages={pageData.total_pages}
          onPageChange={setPage}
          pageSize={pageSize}
          totalItems={pageData.total}
          itemLabel={viewMode === "grouped" ? "villa" : "member"}
          itemsOnPage={
            viewMode === "grouped" ? groupedItems.length : flatRows.length
          }
        />
      )}

      {phoneRejectTarget && (
        <div
          className="vs-login-overlay"
          onClick={() => setPhoneRejectTarget(null)}
        >
          <div
            className="vs-login-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vs-login-header">
              <h2>Reject mobile change?</h2>
              <button
                className="vs-login-close"
                onClick={() => setPhoneRejectTarget(null)}
              >
                ×
              </button>
            </div>
            <p>
              {phoneRejectTarget.user_name} requested{" "}
              <code>+91 {phoneRejectTarget.current_phone}</code> →{" "}
              <code>+91 {phoneRejectTarget.requested_phone}</code>
            </p>
            <div className="vs-field">
              <label>Reason (optional)</label>
              <textarea
                rows={3}
                value={phoneRejectReason}
                onChange={(e) => setPhoneRejectReason(e.target.value)}
                placeholder="Tell the requester why you're rejecting…"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  fontFamily: "inherit",
                  fontSize: 13,
                  outline: "none",
                  boxSizing: "border-box",
                  resize: "vertical",
                }}
              />
            </div>
            <div className="vs-otp-actions">
              <button
                className="vs-btn-secondary"
                onClick={() => setPhoneRejectTarget(null)}
              >
                Cancel
              </button>
              <button
                className="vsa-btn-reject"
                disabled={phoneBusyId === phoneRejectTarget.id}
                onClick={submitPhoneRejection}
              >
                {phoneBusyId === phoneRejectTarget.id
                  ? "Rejecting…"
                  : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejecting && (
        <div className="vs-login-overlay" onClick={() => setRejecting(null)}>
          <div className="vs-login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vs-login-header">
              <h2>Reject Request</h2>
              <button className="vs-login-close" onClick={() => setRejecting(null)}>×</button>
            </div>
            <p>
              Rejecting <strong>{rejecting.user.name}</strong>'s request.
            </p>
            <div className="vs-field">
              <label>Reason for rejection</label>
              <select
                className="vs-input"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              >
                <option value="">Select a reason…</option>
                <option value="Unable to verify villa ownership">Unable to verify villa ownership</option>
                <option value="Duplicate registration">Duplicate registration</option>
                <option value="Invalid information">Invalid information</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="vs-otp-actions">
              <button className="vs-btn-secondary" onClick={() => setRejecting(null)}>Cancel</button>
              <button
                className="vsa-btn-reject"
                disabled={busyId === rejecting.approval_id}
                onClick={submitReject}
              >
                {busyId === rejecting.approval_id ? "Rejecting…" : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
